import * as React from 'react';
import { useIntl } from 'react-intl';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import LoaderService from '../../../Services/LoaderService';
import TooltipComponent from '../../TooltipComponent';
import RangeSelectFilter from '../Filter/RangeSelectFilter';
import useFilter from '../hooks/useFilter';
import { getIsColumnLast } from '../TableHeader/TableHeader';
import { ICheckedTableItems, ITripsOverviewColumn, ITripsOverviewTableData } from '../interfaces';
import { ASCENDING_KEY, CO2_EMISSION_KEY, DESCENDING_KEY } from '../constants';
import {
  checkedTableItemsAtom,
  columnsAtom,
  hasMoreAtom,
  hiddenSummaryItemIndexesAtom,
  offsetAtom,
  rideBookDataAtom,
  rideBookFilterAtom,
  rideItemIdsAtom,
  rideItemTimestampsAtom,
  sortFilterAtom,
  summaryItemIndexesAtom,
} from '../TripsOverviewState';
import { getDataForFilter } from '../TripsOverviewService';
import useAnalytics from '../../../hooks/useAnalytics';

interface IThCo2EmissionProps {
  id: string;
}

function ThCo2Emission(props: IThCo2EmissionProps): JSX.Element {
  const columnsTable = useRecoilValue<ITripsOverviewColumn[]>(columnsAtom);
  const [tableActionsFilters, setTableActionsFilters] = useRecoilState(
    rideBookFilterAtom(props.id)
  );
  const [sortFilter, setSortFilter] = useRecoilState<{ [key: string]: string }>(sortFilterAtom);
  const setRideItemTimestamps = useSetRecoilState<string[]>(rideItemTimestampsAtom);
  const setTableData = useSetRecoilState<ITripsOverviewTableData | null>(rideBookDataAtom);
  const setHasMore = useSetRecoilState<boolean>(hasMoreAtom);
  const setOffset = useSetRecoilState<number>(offsetAtom);
  const setRideItemIds = useSetRecoilState<string[]>(rideItemIdsAtom);
  const setSummaryItemIndexes = useSetRecoilState<number[]>(summaryItemIndexesAtom);
  const setHiddenSummaryItemIndexes = useSetRecoilState<number[]>(hiddenSummaryItemIndexesAtom);
  const setCheckedTableItems = useSetRecoilState<ICheckedTableItems>(checkedTableItemsAtom);
  const { updateFilterItemsState } = useFilter();
  const { trackEvent } = useAnalytics();

  const getFilterData = async () => {
    LoaderService.showLoader(true);
    const dataForFilter = await getDataForFilter(CO2_EMISSION_KEY);

    if (dataForFilter) {
      updateFilterItemsState([
        { id: props.id, isOpen: true, isActionChipsOpen: false, data: dataForFilter, values: null },
      ]);

      LoaderService.showLoader(false);
    } else {
      LoaderService.showLoader(false);
    }
  };

  const { formatMessage: f } = useIntl();
  const handleOnClick = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    event.preventDefault();
    const { id } = event.currentTarget;
    trackEvent(['ride book', 'click', `Ride book open filter ${id}`]);

    setTableActionsFilters(
      (tableActionFilter) =>
        tableActionFilter && {
          ...tableActionFilter,
          id: props.id,
          isOpen: true,
          isActionChipsOpen: false,
        }
    );

    if (id) {
      if (!tableActionsFilters) {
        getFilterData();
      }
    }
  };

  const handleOnSort = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.stopPropagation();
    event.preventDefault();

    if (sortFilter[CO2_EMISSION_KEY] === ASCENDING_KEY) {
      setSortFilter({ [CO2_EMISSION_KEY]: DESCENDING_KEY });
      trackEvent([
        'ride book',
        'click',
        `Ride book sort column ${CO2_EMISSION_KEY} ${DESCENDING_KEY}`,
      ]);
    } else {
      setSortFilter({ [CO2_EMISSION_KEY]: ASCENDING_KEY });
      trackEvent([
        'ride book',
        'click',
        `Ride book sort column ${CO2_EMISSION_KEY} ${ASCENDING_KEY}`,
      ]);
    }
    setTableData(null);
    setRideItemTimestamps([]);
    setSummaryItemIndexes([]);
    setHiddenSummaryItemIndexes([]);
    setRideItemIds([]);
    setOffset(0);
    setHasMore(true);
    setCheckedTableItems({
      mainCheckbox: false,
      intervals: [],
      intervalsBlacklist: [],
      rideIdBlacklist: {},
      rideIds: {},
      customerId: null,
      counts: {},
    });
  };

  return (
    <div
      className={`th-sticky b-table-actions ${
        tableActionsFilters && tableActionsFilters.values ? 'active' : ''
      }`}
      style={{
        minWidth: '90px',
        maxWidth: '90px',
        flex: 1,
        whiteSpace: 'nowrap',
        cursor: 'pointer',
      }}
    >
      <TooltipComponent
        id={`ridebook-${props.id}`}
        overlay={
          <div style={{ color: '#fff', whiteSpace: 'pre-wrap', fontWeight: 'normal' }}>
            {`${f({
              id: 'tables.tripsOverview.header.co2Emission',
            })}: `}
            <strong>
              {`${f({
                id: 'tables.tripsOverview.header.tooltip.co2Emission',
              })}`}
            </strong>
          </div>
        }
        width="300px"
        style={{ top: '35px' }}
        positionLeft={getIsColumnLast(columnsTable, props.id)}
      >
        <div className="d-flex w-100">
          <div
            className="text-truncate"
            id={props.id}
            onClick={handleOnClick}
            data-cy={`filter-button-text-${props.id}`}
          >
            {f({ id: 'tables.tripsOverview.header.co2Emission' })}
          </div>

          <a
            href="#"
            className="filter cmd-icons cmd-icon-filter"
            id={props.id}
            onClick={handleOnClick}
            data-cy={`filter-button-${props.id}`}
          >
            {/* test */}
          </a>
          <a
            id={props.id}
            href="#"
            title={
              sortFilter[CO2_EMISSION_KEY] === ASCENDING_KEY
                ? f({ id: 'table.filter.desc' })
                : f({ id: 'table.filter.asc' })
            }
            className={`${sortFilter[CO2_EMISSION_KEY] ? 'active' : 'inactive'} cmd-icon cmd-icon-sorting${
              sortFilter[CO2_EMISSION_KEY] === ASCENDING_KEY ? '-a-z' : '-z-a'
            }  text-decoration-none position-relative  ml-2`}
            onClick={handleOnSort}
            data-cy={`filter-buttons-sorting-btn-ridebook-${props.id}`}
          >
            {/* test */}
          </a>
          <div />
        </div>
      </TooltipComponent>{' '}
      {tableActionsFilters &&
        tableActionsFilters.isOpen &&
        !tableActionsFilters.isActionChipsOpen &&
        tableActionsFilters.data && (
          <div id="range-select" style={{ position: 'absolute', left: '0px', top: '50px' }}>
            <div className="open">
              <RangeSelectFilter
                id={props.id}
                title={f({ id: 'tables.tripsOverview.column.co2EmissionsCan' })}
                valueFrom={tableActionsFilters.values?.min}
                valueTo={tableActionsFilters.values?.max}
                defaultMinValue={
                  tableActionsFilters.data.minValue ? tableActionsFilters.data.minValue : '0'
                }
                defaultMaxValue={
                  tableActionsFilters.data.maxValue ? tableActionsFilters.data.maxValue : '10'
                }
                suffix="kg"
              />
            </div>
          </div>
        )}
    </div>
  );
}

export default ThCo2Emission;
